import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import { useApi } from '../api/context';
import { PAGE_SIZE, queryKeys, useBlueprint, usePager } from '../api/queries';
import { JsonDiffView } from '../components/JsonDiffView';
import { ErrorMessage, Loading, PageHeader, Pager } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { strings } from '../strings';
import { useCardTable } from '../lib/cardTable';
import { parseCompare } from '../lib/versionCompare';

function VersionDiff({ blueprintId, versions }: { blueprintId: string; versions: [number, number] }) {
  const api = useApi();
  const [older, newer] = [...versions].sort((a, b) => a - b);
  const left = useQuery({
    queryKey: queryKeys.version(blueprintId, older),
    queryFn: () => api.getVersion(blueprintId, older),
  });
  const right = useQuery({
    queryKey: queryKeys.version(blueprintId, newer),
    queryFn: () => api.getVersion(blueprintId, newer),
  });
  if (left.error || right.error) return <ErrorMessage error={left.error ?? right.error} />;
  if (!left.data || !right.data) return <Loading />;
  return (
    <JsonDiffView
      left={left.data.content}
      right={right.data.content}
      leftTitle={strings.versions.left(older)}
      rightTitle={strings.versions.left(newer)}
    />
  );
}

/** `/blueprints/:id/versions`: select two versions for a side-by-side diff (spec 6.3.8), kept in `?compare=`. */
export function VersionsPage() {
  const cardTable = useCardTable();
  const { id = '' } = useParams();
  const api = useApi();
  const blueprint = useBlueprint(id);
  const pager = usePager();
  const versions = useQuery({
    queryKey: queryKeys.versions(id, pager.cursor),
    queryFn: () => api.listVersions(id, { limit: PAGE_SIZE, cursor: pager.cursor }),
    placeholderData: keepPreviousData,
  });
  const [search, setSearch] = useSearchParams();
  const selected = parseCompare(search.get('compare'));

  const toggle = (version: number) => {
    const next = selected.includes(version)
      ? selected.filter((v) => v !== version)
      : [...selected, version].slice(-2);
    const params = new URLSearchParams(search);
    if (next.length > 0) params.set('compare', next.join(','));
    else params.delete('compare');
    setSearch(params, { replace: true });
  };

  return (
    <PageContainer>
      <PageHeader title={`${strings.versions.title}: ${blueprint.data?.data.name ?? ''}`}>
        <Link className="link text-sm" to={`/blueprints/${id}`}>
          {strings.blueprints.designer}
        </Link>
      </PageHeader>
      <p className="help mb-3">{strings.versions.compareHelp}</p>
      {versions.error ? (
        <ErrorMessage error={versions.error} />
      ) : !versions.data ? (
        <Loading />
      ) : (
        <table ref={cardTable} className="table table-cards">
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">{strings.versions.compare}</span>
              </th>
              <th scope="col">{strings.versions.version}</th>
              <th scope="col">{strings.versions.created}</th>
              <th scope="col">{strings.versions.hash}</th>
              <th scope="col">
                <span className="sr-only">{strings.versions.openReadOnly}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {versions.data.items.map((v) => (
              <tr key={v.version}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={strings.versions.selectVersion(v.version)}
                    checked={selected.includes(v.version)}
                    onChange={() => toggle(v.version)}
                  />
                </td>
                <td>{v.version}</td>
                <td>
                  {formatDateTime(v.createdAt)} {strings.common.by(v.createdBy)}
                </td>
                <td className="font-mono text-xs">{v.contentHash.slice(0, 12)}</td>
                <td>
                  <Link className="link" to={`/blueprints/${id}?version=${v.version}`}>
                    {strings.versions.openReadOnly}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pager
        hasPrevious={pager.hasPrevious}
        nextCursor={versions.data?.nextCursor}
        onPrevious={pager.previous}
        onNext={pager.next}
      />
      {selected.length === 2 && (
        <section className="mt-6" aria-label={strings.versions.compare}>
          <VersionDiff blueprintId={id} versions={[selected[0], selected[1]]} />
        </section>
      )}
    </PageContainer>
  );
}
